class FeatureHighlightGallery extends HTMLElement {
  constructor() {
    super();
    this.scrollOffset = 0;
    this.velocity = 0;
    this.isDragging = false;
    this.animationId = null;
    this.setWidth = 0;
    this.itemUnit = 0;
    this.clonesPerSide = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('.fhg__track');
    this.originalItems = Array.from(this.querySelectorAll('.fhg__item'));
    this.handle = this.querySelector('handle-control');

    if (!this.track || this.originalItems.length < 1) return;

    this._measure();
    this._cloneItems();
    this._updatePosition();
    this._bindEvents();
  }

  _measure() {
    this.gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    this.itemWidth = this.originalItems[0].getBoundingClientRect().width;
    this.itemCount = this.originalItems.length;
    this.itemUnit = this.itemWidth + this.gap;
    this.setWidth = this.itemCount * this.itemUnit;
    this.viewportWidth = this.getBoundingClientRect().width;
    this.clonesPerSide = Math.ceil(this.viewportWidth / this.setWidth) + 2;
  }

  _cloneItems() {
    this.querySelectorAll('.fhg__item--clone').forEach((el) => {
      el.remove();
    });

    const track = this.track;
    const items = this.originalItems;

    for (let c = 0; c < this.clonesPerSide; c++) {
      for (let i = items.length - 1; i >= 0; i--) {
        const clone = items[i].cloneNode(true);
        clone.classList.add('fhg__item--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        this._disableCloneFocus(clone);
        track.insertBefore(clone, track.firstChild);
      }
    }

    for (let c = 0; c < this.clonesPerSide; c++) {
      for (const item of items) {
        const clone = item.cloneNode(true);
        clone.classList.add('fhg__item--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        this._disableCloneFocus(clone);
        track.appendChild(clone);
      }
    }
  }

  _normalizeOffset() {
    if (this.setWidth <= 0) return;
    while (this.scrollOffset >= this.setWidth) this.scrollOffset -= this.setWidth;
    while (this.scrollOffset < 0) this.scrollOffset += this.setWidth;
  }

  _updatePosition() {
    this._normalizeOffset();
    const translateX = -(this.clonesPerSide * this.setWidth + this.scrollOffset);
    this.track.style.transform = `translateX(${translateX}px)`;
  }

  _scrollOneItem(direction) {
    cancelAnimationFrame(this.animationId);
    const remainder = ((this.scrollOffset % this.itemUnit) + this.itemUnit) % this.itemUnit;
    let dist;

    if (direction > 0) {
      dist = this.itemUnit - remainder;
      if (dist < 1) dist = this.itemUnit;
    } else {
      dist = remainder;
      if (dist < 1) dist = this.itemUnit;
    }

    const target = this.scrollOffset + direction * dist;
    this._animateTo(target, 500);
  }

  _animateTo(target, duration) {
    cancelAnimationFrame(this.animationId);

    if (this._getReducedMotion()) {
      this.scrollOffset = target;
      this._updatePosition();
      return;
    }

    const start = this.scrollOffset;
    const distance = target - start;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      this.scrollOffset = start + distance * eased;
      this._updatePosition();

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  _bindEvents() {
    if (this.handle) {
      this.handle.addEventListener('handle:left', () => {
        this._scrollOneItem(-1);
      });

      this.handle.addEventListener('handle:right', () => {
        this._scrollOneItem(1);
      });

      this.handle.addEventListener('handle:dragstart', () => {
        cancelAnimationFrame(this.animationId);
        this.isDragging = true;
        this.velocity = 0;
        this.classList.add('is-dragging');
        this._dragLoop();
      });

      this.handle.addEventListener('handle:dragmove', (e) => {
        if (!this.isDragging) return;
        this.velocity = e.detail.displacement * 0.08;
      });

      this.handle.addEventListener('handle:dragend', () => {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.velocity = 0;
        cancelAnimationFrame(this.animationId);
        this.classList.remove('is-dragging');
        this._snapToNearestItem();
      });
    }

    this.addEventListener('click', (e) => {
      if (e.target.closest('handle-control')) return;

      const item = e.target.closest('.fhg__item');

      if (!item) {
        if (this.classList.contains('has-overlay-open')) {
          this._closeAllOverlays();
        }
        return;
      }

      const type = item.getAttribute('data-item-type');

      if (type === 'product') {
        if (item.classList.contains('is-product-open')) {
          if (e.target.closest('.fhg__item-action') || e.target.closest('.fhg__product-shim')) {
            this._closeOverlay(item, 'is-product-open');
          }
        } else if (item.querySelector('.fhg__product-overlay')) {
          this._openOverlay(item, 'is-product-open');
        }
      }

      if (type === 'feature') {
        if (item.classList.contains('is-feature-open')) {
          if (!e.target.closest('a')) {
            this._closeOverlay(item, 'is-feature-open');
          }
        } else if (item.querySelector('.fhg__feature-overlay')) {
          this._openOverlay(item, 'is-feature-open');
        }
      }
    });

    for (const item of this.originalItems) {
      if (!item.getAttribute('tabindex')) item.setAttribute('tabindex', '0');
      if (!item.getAttribute('role')) item.setAttribute('role', 'button');
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          item.click();
        }
      });
    }

    this._resizeObserver = new ResizeObserver(() => {
      this._measure();
      this._cloneItems();
      this._updatePosition();
    });
    this._resizeObserver.observe(this);

    this._onBlockSelect = (e) => {
      if (!this.contains(e.target)) return;
      const index = this.originalItems.indexOf(e.target);
      if (index >= 0) {
        this.scrollOffset = index * this.itemUnit;
        this._updatePosition();
      }
    };
    document.addEventListener('shopify:block:select', this._onBlockSelect);

    this._onKeyDown = (e) => {
      if (e.key === 'Escape' && this.classList.contains('has-overlay-open')) {
        this._closeAllOverlays();
      }
    };
    document.addEventListener('keydown', this._onKeyDown);
  }

  _dragLoop() {
    if (!this.isDragging && Math.abs(this.velocity) < 0.5) {
      this.velocity = 0;
      this._snapToNearestItem();
      return;
    }

    this.scrollOffset += this.velocity;
    this._updatePosition();

    if (!this.isDragging) {
      this.velocity *= 0.92;
    }

    this.animationId = requestAnimationFrame(() => {
      this._dragLoop();
    });
  }

  _snapToNearestItem() {
    if (this.itemUnit <= 0) return;
    const remainder = ((this.scrollOffset % this.itemUnit) + this.itemUnit) % this.itemUnit;
    let target;
    if (remainder < this.itemUnit / 2) {
      target = this.scrollOffset - remainder;
    } else {
      target = this.scrollOffset + (this.itemUnit - remainder);
    }
    if (Math.abs(target - this.scrollOffset) < 1) return;
    this._animateTo(target, 300);
  }

  _disableCloneFocus(clone) {
    // The clone ROOT first, then its descendants. querySelectorAll does not match
    // the element it is called on, so this only ever neutralised the buttons and
    // links INSIDE a clone — while the tile root itself carries tabindex="0" and
    // role="button" (set on the originals in _setup, then copied by cloneNode).
    // Every clone therefore sat in the tab order under aria-hidden="true": axe
    // aria-hidden-focus, and in practice a keyboard user tabbing through the
    // carousel landed on invisible duplicates of tiles they had already passed.
    // tabindex="-1" keeps mouse clicks and programmatic focus working.
    clone.setAttribute('tabindex', '-1');
    const focusable = clone.querySelectorAll('button, a, [tabindex]');
    for (const el of focusable) {
      el.setAttribute('tabindex', '-1');
    }
  }

  _getReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  _openOverlay(item, openClass) {
    // Only one tile open at a time — close any open overlay (feature OR
    // product) before opening this one. Previously only same-type overlays
    // were closed, so a feature and a product could be open simultaneously.
    this._closeAllOverlays();
    item.classList.add(openClass);
    this._setExpanded(item, true);
    this.classList.add('has-overlay-open');
  }

  _closeOverlay(item, openClass) {
    item.classList.remove(openClass);
    this._setExpanded(item, false);
    const anyOpen = this.querySelector('.fhg__item.is-product-open, .fhg__item.is-feature-open');
    if (!anyOpen) {
      this.classList.remove('has-overlay-open');
    }
  }

  _setExpanded(item, expanded) {
    const btn = item.querySelector('.fhg__item-action');
    if (btn) btn.setAttribute('aria-expanded', String(expanded));
  }

  _closeAllOverlays() {
    const open = this.querySelectorAll('.fhg__item.is-product-open, .fhg__item.is-feature-open');
    for (const item of open) {
      const cls = item.classList.contains('is-product-open') ? 'is-product-open' : 'is-feature-open';
      item.classList.remove(cls);
      this._setExpanded(item, false);
    }
    this.classList.remove('has-overlay-open');
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.animationId);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._onBlockSelect) {
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
    }
    if (this._onKeyDown) {
      document.removeEventListener('keydown', this._onKeyDown);
    }
  }
}

customElements.define('feature-highlight-gallery', FeatureHighlightGallery);
